<template>
  <div
    class="preview-right-panel-sidebar bg-light border-left col"
    :class="{ 'hide-sidebar' : !isOpen }"
    ref="sidebar"
  >
    <b-tabs
      vertical
      class="sidebar-tab-nav h-100"
      nav-class="h-100"
      nav-wrapper-class="h-100"
      content-class="pl-3 sidebar-content d-flex h-100 overflow-auto border-left"
      v-model="activeTab"
    >
      <b-tab
        v-for="(tab, i) in getTabs"
        :key="i"
        :active="isActive(i)"
        @click="showSidebar()"
        :button-id="`sidebarTab${tab.name}`"
        :data-test="`creative-sidebar-${tab.name}-panel`"
        class="flex-fill bg-light sidebar-tabs"
      >
        <template
          v-slot:title
        >
          <i
            class="fal"
            :class="`fa-${tab.icon}`"
            :data-test="`creative-sidebar-${tab.name}-button`"
          />
        </template>
        <transition name="fade">
          <component
            :is="tab.component"
            :key="`${tab.component}-${componentKey}`"
            v-show="isOpen"
            @submit="onSubmit"
            @set="setCreativeProperties"
            :creative-set="creative"
            :content-item="contentItem"
            :creative-settings.sync="creativeProperties"
            :read-only="readOnly"
            :editable-properties="templateEditableProperties"
            :is-commenting-disabled="isCommentingDisabled"
          />
        </transition>
        <b-tooltip
          :disabled="!isOpen"
          :target="`sidebarTab${tab.name}`"
          triggers="hover"
          placement="left"
        >
          {{ tab.name }}
        </b-tooltip>
      </b-tab>
      <template v-slot:tabs-start>
        <li
          class="align-self-center p-4"
          data-test="creative-sidebar-expand-button"
        >
          <div
            class=""
            @click="toggleSidebar"
          >
            <i
              :class="['toggle','fal',setToggleIcon]"
            />
          </div>
        </li>
      </template>
    </b-tabs>
  </div>
</template>
<script>
import {
  mapGetters, mapActions, mapMutations, mapState,
} from 'vuex';
import * as types from '@/modules/content/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as templatesTypes from '@/modules/templates/store/types';

import CreativeProperties from '@/modules/content/components/PreviewRightPanel/CreativeProperties';
import CreativeLogo from '@/modules/content/components/PreviewRightPanel/CreativeLogo';
import CreativeFonts from '@/modules/content/components/PreviewRightPanel/CreativeFonts';
import Comments from '@/modules/content/components/PreviewRightPanel/Comments';

export default {
  name: 'Sidebar',
  components: {
    CreativeProperties,
    CreativeLogo,
    CreativeFonts,
    Comments,
  },
  data() {
    return {
      activeTab: 0,
      index: 0,
      componentKey: 0,
      isOpen: false,
      creativeProperties: {
        backgroundColour: '#ffffff',
        headlineFontFamily: 'Roboto',
        headlineFontSize: 22,
        headlineFontColour: '#000000',
        bodyFontFamily: 'Roboto',
        bodyFontSize: 16,
        bodyFontColour: '#000000',
        ctaBackgroundColour: '#e9ecef',
        ctaFontFamily: 'Roboto',
        ctaFontSize: 16,
        ctaFontColour: '#000000',
        clientName: '',
      },
    };
  },
  props: {
    readOnly: {
      type: Array,
      default: () => [],
    },
    viewType: {
      type: String,
      default: 'Editor',
    },
    contentItem: {
      type: Object,
      default: () => {},
    },
    editableProperties: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'campaignFeedId',
      'order',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_IS_VIEW_TYPE,
    ]),
    ...mapGetters('content', {
      getDisplayCopy: types.GET_CURRENT_SELECTED_CREATIVE_DISPLAY_COPY,
      creative: types.GET_CURRENT_SELECTED_CREATIVE,
    }),
    ...mapGetters('templates', {
      templateEditableProperties: templatesTypes.GET_TEMPLATE_EDITABLE_PROPERTIES,
    }),
    isCommentingDisabled() {
      return this.GET_IS_CAMPAIGN_READONLY && !this.GET_IS_VIEW_TYPE('Showcase');
    },
    setToggleIcon() {
      return this.isOpen ? 'fa-chevron-double-right' : 'fa-chevron-double-left';
    },
    getTabs() {
      const tabs = [];

      if (this.viewType === 'Setup' || this.viewType === 'Editor') {
        tabs.push({
          icon: 'paintbrush-pencil',
          component: 'CreativeProperties',
          name: 'Properties',
        },
        {
          icon: 'font-case',
          component: 'CreativeFonts',
          name: 'Fonts',
        });
      }

      if (this.templateEditableProperties.includes('logo')) {
        tabs.push({
          icon: 'image',
          component: 'CreativeLogo',
          name: 'Logo',
        });
      }

      tabs.push({
        icon: 'comments-alt',
        component: 'Comments',
        name: 'Comments',
      });

      return tabs;
    },
  },
  methods: {
    ...mapActions('content', [
      types.PATCH_CREATIVE,
      types.REMOVE_CURRENT_ANNOTATION,
    ]),
    ...mapMutations('content', [
      types.TOGGLE_ANNOTATIONS,
      types.SET_CREATIVE_SIDEBAR_IS_OPEN,
    ]),
    onSubmit(payload) {
      this.$emit('iframeLoading');
      const creativePayload = {
        showMessage: true,
        campaignId: this.campaign.id,
        feedId: this.campaignFeedId,
        orderId: this.order.id,
        ...payload,
      };
      creativePayload.id = this.creative.id;
      this.PATCH_CREATIVE(creativePayload)
        .then((creativeProperties) => {
          this.creativeProperties = { ...creativeProperties.creatives[0].properties };
          this.$emit('iframeLoad');
        });
    },

    onClickOutside(event) {
      if (event.target.classList[0] === 'annotation-wrapper') {
        return;
      }
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
        this.REMOVE_CURRENT_ANNOTATION();
      }
    },
    isActive(index) {
      return (this.index === index);
    },
    toggleSidebar() {
      this.isOpen ? this.hideSidebar() : this.showSidebar();
    },
    showSidebar() {
      if (this.contentItem) {
        document.addEventListener('click', this.documentClick);
        this.isOpen = true;
        this.SET_CREATIVE_SIDEBAR_IS_OPEN(true);
      }
    },
    hideSidebar() {
      document.removeEventListener('click', this.documentClick);
      this.isOpen = false;
      this.SET_CREATIVE_SIDEBAR_IS_OPEN(false);
    },
    showTooltip(index) {
      return (this.index === index);
    },
    setCreativeProperties() {
      const backgroundColour = this.creative?.properties?.backgroundColour
        ? this.creative.properties.backgroundColour : '#ffffff';
      const {
        headlineFontColour,
        headlineFontFamily,
        headlineFontSize,
        bodyFontColour,
        bodyFontFamily,
        bodyFontSize,
        ctaBackgroundColour,
        ctaFontColour,
        ctaFontFamily,
        ctaFontSize,
        clientName,
      } = this.creative.properties;

      this.creativeProperties = {
        backgroundColour,
        headlineFontColour,
        headlineFontFamily,
        headlineFontSize,
        bodyFontColour,
        bodyFontFamily,
        bodyFontSize,
        ctaBackgroundColour,
        ctaFontColour,
        ctaFontFamily,
        ctaFontSize,
        clientName,
      };
      this.componentKey++;
    },
    documentClick(e) {
      const el = this.$refs.sidebar;
      if (!el) return;
      const { target } = e;
      const isAnnotationWrapper = (e.target.className === 'annotation-wrapper');
      if (el !== target && !el.contains(target) && !isAnnotationWrapper) {
        this.hideSidebar();
      }
    },
  },
  created() {
    if (this.creative.id !== 0 && this.creative.properties) {
      this.setCreativeProperties();
    }
  },
  watch: {
    isOpen() {
      if (this.getTabs[this.activeTab].name === 'Comments' && this.isOpen) this.TOGGLE_ANNOTATIONS(true);
      else {
        this.TOGGLE_ANNOTATIONS(false);
        this.REMOVE_CURRENT_ANNOTATION();
      }
    },
    activeTab() {
      if (this.getTabs[this.activeTab].name === 'Comments' && this.isOpen) this.TOGGLE_ANNOTATIONS(true);
      else {
        this.TOGGLE_ANNOTATIONS(false);
        this.REMOVE_CURRENT_ANNOTATION();
      }
    },
    creative() {
      this.setCreativeProperties();
    },
  },
  mounted() {
    // document.getElementById('view-panel-wrapper').addEventListener('click', this.onClickOutside, false);
  },

};
</script>
<style lang="scss">
.preview-right-panel-sidebar{
  width: 371px;
  // height: calc(100% - 55px);
  margin-right: 0;
  position: absolute;
  top: 0px;
  right: 0px;
  bottom: 0px;
  transition: margin .5s;
  z-index: 1;
  .nav-tabs .nav-item {
    i {
    margin: 0 auto;
    font-size: 1.5rem;
    width: 2rem;
    text-align: center;
    display: block;
    }
  }
  .nav-item {
    padding: 0;
    width: 58px;
    .nav-link {
      width: 100%;
      border: 0;
      color:#47474E;
    }
  }
  &.hide-sidebar{
    margin-right: -312px;
  }
}
.sidebar-tabs{
  max-width: 100%;
}
</style>
